import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, apiErrorMessage, setToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // states: null (checking) | user object | false (unauthenticated)
  const [user, setUser] = useState(null);
  const [error, setError] = useState("");

  const clearSession = useCallback(() => {
    setToken(null);
    setUser(false);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (e) {
      if (e?.response?.status === 401) clearSession();
      else setUser(false);
      return null;
    }
  }, [clearSession]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setToken(data.access_token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      const msg = apiErrorMessage(e, "Gagal masuk. Coba lagi.");
      setError(msg);
      return { ok: false, error: msg };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setToken(null);
    setUser(false);
  }, []);

  return (
    <AuthContext.Provider value={{ user, error, login, logout, refresh, setUser, clearSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside <AuthProvider/>");
  return ctx;
}
